import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import { useTranslation } from "react-i18next";
import CommonModal from "components/common/CommonModal";
import EnhancedTable from "components/common/EnhancedTable";
import ActionButtons from "components/common/ActionButtons";
import { useError } from "contexts/ErrorContext"; // Assume standard hook or error boundary
import {
  getPlatformSummary,
  purchaseCredits,
  getAllSchoolWallets,
  assignCredits,
  toggleServiceEnabled,
  getSchoolTransactions,
  getPendingOrders,
  approveOrder,
  rejectOrder,
} from "services/platformCreditService";

interface PlatformSummary {
  smsTotalPurchased: number;
  smsTotalAssigned: number;
  smsAvailable: number;
  waTotalPurchased: number;
  waTotalAssigned: number;
  waAvailable: number;
}

interface SchoolWallet {
  schoolId: number;
  schoolName: string;
  smsCredits: number;
  whatsappCredits: number;
  smsServiceEnabled: boolean;
  whatsappServiceEnabled: boolean;
}

const loadRazorpay = (): Promise<boolean> =>
  new Promise((resolve) => {
    if ((window as any).Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const MessagingCreditsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [platformSummary, setPlatformSummary] = useState<PlatformSummary | null>(null);
  const [schoolWallets, setSchoolWallets] = useState<SchoolWallet[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingSchools, setLoadingSchools] = useState(true);

  // Buy Credits Modal State
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [buyChannel, setBuyChannel] = useState<"SMS" | "WHATSAPP">("SMS");
  const [buyAmount, setBuyAmount] = useState<number>(1000);
  const [providerRef, setProviderRef] = useState("");
  const [submittingBuy, setSubmittingBuy] = useState(false);
  const [buyMode, setBuyMode] = useState<"ADD" | "DEDUCT">("ADD");

  // Assign Credits Modal State
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<SchoolWallet | null>(null);
  const [assignChannel, setAssignChannel] = useState<"SMS" | "WHATSAPP">("SMS");
  const [assignAmount, setAssignAmount] = useState<number>(100);
  const [submittingAssign, setSubmittingAssign] = useState(false);
  const [assignMode, setAssignMode] = useState<"ADD" | "DEDUCT">("ADD");

  // Transaction History State
  const [selectedHistorySchoolId, setSelectedHistorySchoolId] = useState<number | "">("");
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [txPage, setTxPage] = useState(0);
  const [txRowsPerPage, setTxRowsPerPage] = useState(10);
  const [txTotalElements, setTxTotalElements] = useState(0);

  // Simple local alerts
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Pending Stripe Orders State
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);
  const [loadingPendingOrders, setLoadingPendingOrders] = useState(true);
  const [pendingOrdersPage, setPendingOrdersPage] = useState(0);
  const [pendingOrdersRowsPerPage, setPendingOrdersRowsPerPage] = useState(10);
  const [pendingOrdersTotalElements, setPendingOrdersTotalElements] = useState(0);

  // Approve Dialog State
  const [approveOpen, setApproveOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [assignCreditAmount, setAssignCreditAmount] = useState<number>(0);
  const [submittingApprove, setSubmittingApprove] = useState(false);

  // Reject Dialog State
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submittingReject, setSubmittingReject] = useState(false);

  const triggerToast = (msg: string, isError = false) => {
    if (isError) {
      setErrorMsg(msg);
      setSuccessMsg(null);
    } else {
      setSuccessMsg(msg);
      setErrorMsg(null);
    }
    setTimeout(() => {
      setSuccessMsg(null);
      setErrorMsg(null);
    }, 4000);
  };

  const fetchSummary = async () => {
    try {
      setLoadingSummary(true);
      const res = await getPlatformSummary();
      setPlatformSummary(res.data);
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to load platform summary", true);
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchSchools = async () => {
    try {
      setLoadingSchools(true);
      const res = await getAllSchoolWallets();
      setSchoolWallets(res.data);
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to load school wallets", true);
    } finally {
      setLoadingSchools(false);
    }
  };

  const fetchTransactions = async (schoolId: number, page = 0, size = 10) => {
    try {
      setLoadingTransactions(true);
      const res = await getSchoolTransactions(schoolId, page, size);
      setTransactions(res.data.content || []);
      setTxTotalElements(res.data.totalElements || 0);
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to load transaction history", true);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchSchools();
    fetchPendingOrders();
  }, []);

  useEffect(() => {
    if (selectedHistorySchoolId !== "") {
      fetchTransactions(Number(selectedHistorySchoolId), txPage, txRowsPerPage);
    } else {
      setTransactions([]);
      setTxTotalElements(0);
    }
  }, [selectedHistorySchoolId, txPage, txRowsPerPage]);

  const handleBuyOpen = (channel: "SMS" | "WHATSAPP") => {
    setBuyChannel(channel);
    setBuyAmount(1000);
    setProviderRef("");
    setBuyMode("ADD");
    setBuyModalOpen(true);
  };

  const handleBuySubmit = async () => {
    if (buyAmount <= 0) {
      triggerToast("Amount must be greater than 0", true);
      return;
    }
    const finalAmount = buyMode === "ADD" ? buyAmount : -buyAmount;

    if (buyMode === "DEDUCT") {
      const poolAvailable = buyChannel === "SMS" ? (platformSummary?.smsAvailable ?? 0) : (platformSummary?.waAvailable ?? 0);
      if (buyAmount > poolAvailable) {
        triggerToast(`Cannot deduct more credits than platform available pool: ${poolAvailable}`, true);
        return;
      }
    }

    try {
      setSubmittingBuy(true);
      const res = await purchaseCredits(buyChannel, finalAmount, providerRef);
      setPlatformSummary(res.data);
      triggerToast(`Successfully recorded manual ${buyMode === "ADD" ? "addition" : "deduction"} of ${buyAmount} credits for ${buyChannel}`);
      setBuyModalOpen(false);
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to adjust credits", true);
    } finally {
      setSubmittingBuy(false);
    }
  };

  const handleAssignOpen = (school: SchoolWallet) => {
    setSelectedSchool(school);
    setAssignChannel("SMS");
    setAssignAmount(100);
    setAssignMode("ADD");
    setAssignModalOpen(true);
  };

  const handleAssignSubmit = async () => {
    if (!selectedSchool) return;
    if (assignAmount <= 0) {
      triggerToast("Amount must be greater than 0", true);
      return;
    }
    const finalAmount = assignMode === "ADD" ? assignAmount : -assignAmount;

    if (assignMode === "ADD") {
      const available = assignChannel === "SMS" ? (platformSummary?.smsAvailable ?? 0) : (platformSummary?.waAvailable ?? 0);
      if (assignAmount > available) {
        triggerToast(`Insufficient credits in platform pool. Max available: ${available}`, true);
        return;
      }
    } else {
      const current = assignChannel === "SMS" ? selectedSchool.smsCredits : selectedSchool.whatsappCredits;
      if (assignAmount > current) {
        triggerToast(`Cannot deduct more credits than the school possesses. Max available: ${current}`, true);
        return;
      }
    }

    try {
      setSubmittingAssign(true);
      await assignCredits(selectedSchool.schoolId, assignChannel, finalAmount);
      triggerToast(`${assignMode === "ADD" ? "Assigned" : "Deducted"} ${assignAmount} ${assignChannel} credits ${assignMode === "ADD" ? "to" : "from"} ${selectedSchool.schoolName}`);
      setAssignModalOpen(false);
      // Refresh
      fetchSummary();
      fetchSchools();
      if (Number(selectedHistorySchoolId) === selectedSchool.schoolId) {
        fetchTransactions(selectedSchool.schoolId, txPage, txRowsPerPage);
      }
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to adjust credits", true);
    } finally {
      setSubmittingAssign(false);
    }
  };

  const fetchPendingOrders = async (p = pendingOrdersPage, s = pendingOrdersRowsPerPage) => {
    try {
      setLoadingPendingOrders(true);
      const res = await getPendingOrders(p, s);
      setPendingOrders(res.data.content || []);
      setPendingOrdersTotalElements(res.data.totalElements || 0);
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to load pending orders", true);
    } finally {
      setLoadingPendingOrders(false);
    }
  };

  const handleApproveOpen = (order: any) => {
    setSelectedOrder(order);
    setAssignCreditAmount(Math.round(order.amountPaidRupees));
    setApproveOpen(true);
  };

  const handleApproveSubmit = async () => {
    if (!selectedOrder) return;
    if (assignCreditAmount <= 0) {
      triggerToast("Assigned credits must be greater than 0", true);
      return;
    }
    const poolAvailable = selectedOrder.channel === "SMS" ? (platformSummary?.smsAvailable ?? 0) : (platformSummary?.waAvailable ?? 0);
    if (assignCreditAmount > poolAvailable) {
      triggerToast(`Insufficient credits in platform pool. Max available: ${poolAvailable}`, true);
      return;
    }

    try {
      setSubmittingApprove(true);
      await approveOrder(selectedOrder.id, assignCreditAmount);
      triggerToast(`Successfully approved order #${selectedOrder.id} and assigned ${assignCreditAmount} credits.`);
      setApproveOpen(false);
      
      // Refresh all summaries and tables
      fetchPendingOrders();
      fetchSummary();
      fetchSchools();
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to approve order", true);
    } finally {
      setSubmittingApprove(false);
    }
  };

  const handleRejectOpen = (order: any) => {
    setSelectedOrder(order);
    setRejectionReason("");
    setRejectOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!selectedOrder) return;
    if (!rejectionReason.trim()) {
      triggerToast("Rejection reason is required", true);
      return;
    }

    try {
      setSubmittingReject(true);
      await rejectOrder(selectedOrder.id, rejectionReason);
      triggerToast(`Successfully rejected order #${selectedOrder.id}.`);
      setRejectOpen(false);
      
      // Refresh
      fetchPendingOrders();
      fetchSummary();
      fetchSchools();
    } catch (err: any) {
      triggerToast(err.response?.data?.message || "Failed to reject order", true);
    } finally {
      setSubmittingReject(false);
    }
  };

  const handleToggleChannel = async (schoolId: number, channel: "SMS" | "WHATSAPP", currentVal: boolean) => {
    const newVal = !currentVal;
    // Optimistic Update
    setSchoolWallets((prev) =>
      prev.map((s) => {
        if (s.schoolId === schoolId) {
          return {
            ...s,
            smsServiceEnabled: channel === "SMS" ? newVal : s.smsServiceEnabled,
            whatsappServiceEnabled: channel === "WHATSAPP" ? newVal : s.whatsappServiceEnabled,
          };
        }
        return s;
      })
    );

    try {
      await toggleServiceEnabled(schoolId, channel, newVal);
      triggerToast(`${channel} service ${newVal ? "enabled" : "disabled"} successfully`);
    } catch (err: any) {
      // Revert optimistic update
      setSchoolWallets((prev) =>
        prev.map((s) => {
          if (s.schoolId === schoolId) {
            return {
              ...s,
              smsServiceEnabled: channel === "SMS" ? currentVal : s.smsServiceEnabled,
              whatsappServiceEnabled: channel === "WHATSAPP" ? currentVal : s.whatsappServiceEnabled,
            };
          }
          return s;
        })
      );
      triggerToast(err.response?.data?.message || "Failed to update service status", true);
    }
  };

  // School wallets table column definitions
  const columns = [
    { id: "schoolName", label: t("school_name", "School Name"), minWidth: 200 },
    {
      id: "smsCredits",
      label: t("sms_credits", "SMS Credits"),
      align: "center" as const,
      format: (val: any) => val.toLocaleString(),
    },
    {
      id: "whatsappCredits",
      label: t("wa_credits", "WhatsApp Credits"),
      align: "center" as const,
      format: (val: any) => val.toLocaleString(),
    },
    {
      id: "smsServiceEnabled",
      label: t("sms_enabled", "SMS Enabled"),
      align: "center" as const,
      format: (val: any, row: any) => (
        <Switch
          checked={Boolean(val)}
          onChange={() => handleToggleChannel(row.schoolId, "SMS", Boolean(val))}
          color="primary"
        />
      ),
    },
    {
      id: "whatsappServiceEnabled",
      label: t("wa_enabled", "WA Enabled"),
      align: "center" as const,
      format: (val: any, row: any) => (
        <Switch
          checked={Boolean(val)}
          onChange={() => handleToggleChannel(row.schoolId, "WHATSAPP", Boolean(val))}
          color="success"
        />
      ),
    },
  ];

  const pendingOrderColumns = [
    { id: "id", label: "Order ID", align: "center" as const },
    { id: "schoolName", label: "School Name" },
    { id: "channel", label: "Channel" },
    {
      id: "amountPaidRupees",
      label: "Amount Paid",
      format: (val: any) => `₹${val.toFixed(2)}`,
    },
    {
      id: "createdAt",
      label: "Date Paid",
      format: (val: any) => (val ? new Date(val).toLocaleString() : ""),
    },
    {
      id: "requestedByName",
      label: "Requested By",
    },
  ];

  // Transaction history table column definitions
  const txColumns = [
    {
      id: "createdAt",
      label: t("date", "Date"),
      format: (val: any) => (val ? new Date(val).toLocaleString() : ""),
    },
    { id: "channel", label: t("channel", "Channel") },
    {
      id: "type",
      label: t("type", "Type"),
      format: (val: any) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: val === "CREDIT" ? "success.main" : "error.main",
          }}
        >
          {val}
        </Typography>
      ),
    },
    {
      id: "amount",
      label: t("amount", "Amount"),
      format: (val: any) => val.toLocaleString(),
    },
    { id: "note", label: t("note", "Note") },
    { id: "createdBy", label: t("created_by", "Created By ID"), format: (val: any) => val || "SYSTEM" },
  ];

  return (
    <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CreditScoreIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {t("messaging_credits", "Messaging Credits Management")}
        </Typography>
      </Box>

      {/* SUCCESS / ERROR TOAST ALERTS */}
      {successMsg && <Alert severity="success">{successMsg}</Alert>}
      {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

      {/* SECTION A — PLATFORM CREDIT POOLS */}
      <Grid container spacing={3}>
        {/* SMS POOL CARD */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
              color: "#fff",
              borderRadius: 3,
              boxShadow: "0 8px 30px rgba(30, 60, 114, 0.2)",
              transition: "transform 0.3s ease",
              "&:hover": { transform: "scale(1.02)" },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 600 }}>
                SMS Credits (MSG91 Pool)
              </Typography>
              {loadingSummary ? (
                <CircularProgress color="inherit" size={30} sx={{ my: 2 }} />
              ) : (
                <Box sx={{ my: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>
                    {platformSummary?.smsAvailable.toLocaleString()}
                    <span style={{ fontSize: "1rem", fontWeight: 400, opacity: 0.7 }}> available</span>
                  </Typography>
                  <Stack direction="row" spacing={3} sx={{ mt: 1.5, opacity: 0.9 }}>
                    <Typography variant="body2">
                      Purchased: <strong>{platformSummary?.smsTotalPurchased.toLocaleString()}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Assigned: <strong>{platformSummary?.smsTotalAssigned.toLocaleString()}</strong>
                    </Typography>
                  </Stack>
                </Box>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleBuyOpen("SMS")}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  textTransform: "none",
                  borderRadius: 2,
                  mt: 1,
                }}
              >
                + Buy SMS Credits
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* WHATSAPP POOL CARD */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)",
              color: "#fff",
              borderRadius: 3,
              boxShadow: "0 8px 30px rgba(15, 32, 39, 0.2)",
              transition: "transform 0.3s ease",
              "&:hover": { transform: "scale(1.02)" },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 600 }}>
                WhatsApp Credits (Wassenger Pool)
              </Typography>
              {loadingSummary ? (
                <CircularProgress color="inherit" size={30} sx={{ my: 2 }} />
              ) : (
                <Box sx={{ my: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>
                    {platformSummary?.waAvailable.toLocaleString()}
                    <span style={{ fontSize: "1rem", fontWeight: 400, opacity: 0.7 }}> available</span>
                  </Typography>
                  <Stack direction="row" spacing={3} sx={{ mt: 1.5, opacity: 0.9 }}>
                    <Typography variant="body2">
                      Purchased: <strong>{platformSummary?.waTotalPurchased.toLocaleString()}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Assigned: <strong>{platformSummary?.waTotalAssigned.toLocaleString()}</strong>
                    </Typography>
                  </Stack>
                </Box>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleBuyOpen("WHATSAPP")}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                  textTransform: "none",
                  borderRadius: 2,
                  mt: 1,
                }}
              >
                + Buy WhatsApp Credits
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SECTION B — PENDING DEPOSIT ORDERS */}
      <Box sx={{ mt: 2 }}>
        <EnhancedTable
          title="Pending Stripe Deposit Orders"
          columns={pendingOrderColumns}
          data={pendingOrders}
          loading={loadingPendingOrders}
          enablePagination={true}
          page={pendingOrdersPage}
          rowsPerPage={pendingOrdersRowsPerPage}
          totalElements={pendingOrdersTotalElements}
          onPageChange={(e, newPage) => {
            setPendingOrdersPage(newPage);
            fetchPendingOrders(newPage, pendingOrdersRowsPerPage);
          }}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            setPendingOrdersRowsPerPage(newSize);
            setPendingOrdersPage(0);
            fetchPendingOrders(0, newSize);
          }}
          actions={(row: any) => (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => handleApproveOpen(row)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handleRejectOpen(row)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Reject
              </Button>
            </Stack>
          )}
          cardProps={{
            elevation: 3,
            sx: { borderRadius: 3 },
          }}
        />
      </Box>

      {/* SECTION B — SCHOOL BALANCES TABLE */}
      <Box>
        <EnhancedTable
          title="School Credit Balances"
          columns={columns}
          data={schoolWallets}
          loading={loadingSchools}
          actions={(row: any) => (
            <ActionButtons
              onEdit={() => handleAssignOpen(row)}
              editTooltip="Assign Credits"
              showDelete={false}
            />
          )}
          cardProps={{
            elevation: 3,
            sx: { borderRadius: 3, p: 1 },
          }}
        />
      </Box>

      {/* SECTION C — TRANSACTION HISTORY COLLAPSIBLE */}
      <Accordion sx={{ borderRadius: 3, boxShadow: 3, "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            View Transaction History
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <FormControl fullWidth size="small" sx={{ maxWidth: 300 }}>
            <InputLabel id="history-school-select-label">Select School</InputLabel>
            <Select
              labelId="history-school-select-label"
              value={selectedHistorySchoolId}
              label="Select School"
              onChange={(e) => {
                setSelectedHistorySchoolId(e.target.value as number);
                setTxPage(0);
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {schoolWallets.map((s) => (
                <MenuItem key={s.schoolId} value={s.schoolId}>
                  {s.schoolName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedHistorySchoolId !== "" ? (
            <EnhancedTable
              columns={txColumns}
              data={transactions}
              loading={loadingTransactions}
              enablePagination={true}
              page={txPage}
              rowsPerPage={txRowsPerPage}
              totalElements={txTotalElements}
              onPageChange={(e, newPage) => setTxPage(newPage)}
              onRowsPerPageChange={(e) => {
                setTxRowsPerPage(parseInt(e.target.value, 10));
                setTxPage(0);
              }}
              cardProps={{
                elevation: 0,
                sx: { border: "1px solid #e0e0e0" },
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              Please select a school to load its wallet transaction history.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* BUY CREDITS MODAL */}
      <CommonModal
        open={buyModalOpen}
        onClose={() => !submittingBuy && setBuyModalOpen(false)}
        title="Record Manual Platform Pool Adjustment"
        maxWidth="xs"
        contentSx={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: "16px !important" }}
        customActions={
          <>
            <Button
              onClick={() => setBuyModalOpen(false)}
              variant="outlined"
              disabled={submittingBuy}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBuySubmit}
              variant="contained"
              color={buyMode === "ADD" ? "primary" : "error"}
              disabled={submittingBuy}
              startIcon={submittingBuy ? <CircularProgress size={20} /> : <SendIcon />}
            >
              {submittingBuy ? "Processing..." : buyMode === "ADD" ? "Add Credits" : "Deduct Credits"}
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary">
          Adjust the platform's manual pool credits for {buyChannel}.
        </Typography>
        <ToggleButtonGroup
          color="primary"
          value={buyMode}
          exclusive
          onChange={(e, val) => val && setBuyMode(val)}
          fullWidth
          size="small"
        >
          <ToggleButton value="ADD">Add Credits</ToggleButton>
          <ToggleButton value="DEDUCT">Deduct Credits</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          label="Amount (Credits)"
          type="number"
          value={buyAmount}
          onChange={(e) => setBuyAmount(Math.max(1, Number(e.target.value)))}
          fullWidth
          size="small"
          inputProps={{ min: 1 }}
        />
        <TextField
          label="Reference / Provider Note"
          placeholder="e.g. MSG91 purchase receipt number"
          value={providerRef}
          onChange={(e) => setProviderRef(e.target.value)}
          fullWidth
          size="small"
        />
      </CommonModal>

      {/* ASSIGN CREDITS MODAL */}
      <CommonModal
        open={assignModalOpen}
        onClose={() => !submittingAssign && setAssignModalOpen(false)}
        title="Adjust School Credits"
        maxWidth="xs"
        contentSx={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: "16px !important" }}
        customActions={
          <>
            <Button
              onClick={() => setAssignModalOpen(false)}
              variant="outlined"
              disabled={submittingAssign}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignSubmit}
              variant="contained"
              color={assignMode === "ADD" ? "primary" : "error"}
              disabled={submittingAssign}
              startIcon={submittingAssign ? <CircularProgress size={20} /> : <SendIcon />}
            >
              {assignMode === "ADD" ? "Assign" : "Deduct"}
            </Button>
          </>
        }
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
          {selectedSchool?.schoolName}
        </Typography>
        <ToggleButtonGroup
          color="primary"
          value={assignMode}
          exclusive
          onChange={(e, val) => val && setAssignMode(val)}
          fullWidth
          size="small"
        >
          <ToggleButton value="ADD">Add/Assign Credits</ToggleButton>
          <ToggleButton value="DEDUCT">Deduct Credits</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          color="primary"
          value={assignChannel}
          exclusive
          onChange={(e, val) => val && setAssignChannel(val)}
          fullWidth
          size="small"
        >
          <ToggleButton value="SMS">SMS Credits</ToggleButton>
          <ToggleButton value="WHATSAPP">WhatsApp Credits</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          label="Amount"
          type="number"
          value={assignAmount}
          onChange={(e) => setAssignAmount(Math.max(1, Number(e.target.value)))}
          fullWidth
          size="small"
          inputProps={{ min: 1 }}
        />
        <Box>
          <Typography variant="body2" color="text.secondary">
            Platform Pool Available:{" "}
            <strong>
              {assignChannel === "SMS"
                ? platformSummary?.smsAvailable.toLocaleString()
                : platformSummary?.waAvailable.toLocaleString()}{" "}
              credits
            </strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            After adjust, school will have:{" "}
            <strong>
              {assignChannel === "SMS"
                ? (assignMode === "ADD"
                    ? ((selectedSchool?.smsCredits ?? 0) + assignAmount)
                    : ((selectedSchool?.smsCredits ?? 0) - assignAmount)
                  ).toLocaleString()
                : (assignMode === "ADD"
                    ? ((selectedSchool?.whatsappCredits ?? 0) + assignAmount)
                    : ((selectedSchool?.whatsappCredits ?? 0) - assignAmount)
                  ).toLocaleString()}{" "}
              credits
            </strong>
          </Typography>
        </Box>
      </CommonModal>

      {/* APPROVE ORDER DIALOG */}
      <CommonModal
        open={approveOpen}
        onClose={() => !submittingApprove && setApproveOpen(false)}
        title="Approve Credit Purchase Order"
        maxWidth="xs"
        contentSx={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: "16px !important" }}
        customActions={
          <>
            <Button
              onClick={() => setApproveOpen(false)}
              variant="outlined"
              disabled={submittingApprove}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveSubmit}
              variant="contained"
              color="success"
              disabled={submittingApprove}
              startIcon={submittingApprove ? <CircularProgress size={20} /> : <CreditScoreIcon />}
            >
              Approve & Grant
            </Button>
          </>
        }
      >
        {selectedOrder && (
          <>
            <Box>
              <Typography variant="body2" color="text.secondary">School Name</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.schoolName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Requested Channel / Amount Paid</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {selectedOrder.channel} / ₹{selectedOrder.amountPaidRupees.toFixed(2)}
              </Typography>
            </Box>
            <TextField
              label="Assign Credits"
              type="number"
              value={assignCreditAmount}
              onChange={(e) => setAssignCreditAmount(Math.max(1, Number(e.target.value)))}
              fullWidth
              size="small"
              inputProps={{ min: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Platform Pool Available:{" "}
              <strong>
                {selectedOrder.channel === "SMS"
                  ? platformSummary?.smsAvailable.toLocaleString()
                  : platformSummary?.waAvailable.toLocaleString()}{" "}
                credits
              </strong>
            </Typography>
          </>
        )}
      </CommonModal>

      {/* REJECT ORDER DIALOG */}
      <CommonModal
        open={rejectOpen}
        onClose={() => !submittingReject && setRejectOpen(false)}
        title="Reject Credit Purchase Order"
        maxWidth="xs"
        contentSx={{ display: "flex", flexDirection: "column", gap: 3, paddingTop: "16px !important" }}
        customActions={
          <>
            <Button
              onClick={() => setRejectOpen(false)}
              variant="outlined"
              disabled={submittingReject}
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectSubmit}
              variant="contained"
              color="error"
              disabled={submittingReject}
              startIcon={submittingReject ? <CircularProgress size={20} /> : <SendIcon />}
            >
              Reject Order
            </Button>
          </>
        }
      >
        {selectedOrder && (
          <>
            <Box>
              <Typography variant="body2" color="text.secondary">School Name</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.schoolName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Requested Channel / Amount Paid</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {selectedOrder.channel} / ₹{selectedOrder.amountPaidRupees.toFixed(2)}
              </Typography>
            </Box>
            <TextField
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
          </>
        )}
      </CommonModal>
    </Box>
  );
};

export default MessagingCreditsManagement;
